/**
 * Tax-loss harvest plan contract (docs/roadmap/tasks/TAX_LOSS_HARVESTING.md, TLH-01).
 *
 * The API computes everything; these are the shapes it returns. Every amount
 * is in dollars. Losses are NEGATIVE (`unrealizedGain`, `realizedLoss`,
 * `totalLoss`); savings are POSITIVE when the plan lowers a figure.
 */

import type { SafeHarborDetermination, TaxQuarterLabel } from './taxCalculation.js';

/**
 * What the plan tries to offset:
 * - `offsetShortTerm`: drive the taxable short-term gain (ordinary rates) to zero.
 * - `offsetAllGains`: drive all taxable capital gain, short- and long-term, to zero.
 * - `maxDeduction`: all gains plus the full ordinary-income loss deduction ($3,000, $1,500 MFS).
 */
export const HARVEST_TARGETS = ['offsetShortTerm', 'offsetAllGains', 'maxDeduction'] as const;
export type HarvestTarget = (typeof HARVEST_TARGETS)[number];

export const DEFAULT_HARVEST_TARGET: HarvestTarget = 'offsetShortTerm';

export type HarvestTerm = 'short' | 'long';

/**
 * - `eligible`: may be sold by the plan.
 * - `washSaleBlocked`: the symbol was acquired in the last 30 days (any account), so a loss now would be disallowed.
 *
 * Positions whose lots cannot be trusted (unknown basis, unreconciled
 * history, no live quote, …) are listed in `excludedPositions` instead.
 */
export type HarvestLotStatus = 'eligible' | 'washSaleBlocked';

/** One open tax lot carrying an unrealized loss, in a taxable account. */
export interface HarvestLot {
  /** Ledger lot id (`tx:<uuid>` or `manual:<uuid>`), the id specific-lot picks use. */
  lotId: string;
  symbol: string;
  description?: string;
  securityType: string;
  accountId: string;
  accountName: string;
  /** Real purchase date. */
  acquisitionDate: string;
  /** Date the holding period runs from — earlier than the purchase when a wash sale carried days over. */
  holdingPeriodStartDate: string;
  term: HarvestTerm;
  /** Days until the lot turns long-term; null when it already is. */
  daysUntilLongTerm: number | null;
  quantity: number;
  costBasisPerShare: number;
  currentPrice: number;
  costBasis: number;
  marketValue: number;
  /** Market value − cost basis. Negative for every lot in a plan. */
  unrealizedGain: number;
  /** |unrealizedGain| / marketValue: loss harvested per dollar sold. */
  lossPerDollar: number;
  status: HarvestLotStatus;
  /** Why the lot is not eligible; null when it is. */
  statusReason: string | null;
  /** First day a sale would not be washed by a recent purchase; null when not blocked. */
  harvestableAfter: string | null;
}

/** One recommended sale: all or part of a lot. */
export interface HarvestSale {
  lotId: string;
  symbol: string;
  accountId: string;
  accountName: string;
  term: HarvestTerm;
  acquisitionDate: string;
  sharesToSell: number;
  isFullLot: boolean;
  price: number;
  proceeds: number;
  costBasis: number;
  /** proceeds − costBasis. Negative. */
  realizedLoss: number;
}

/** The next estimated-payment installment (federal Schedule AI + state regular method). */
export interface HarvestInstallment {
  quarter: TaxQuarterLabel;
  /** YYYY-MM-DD, rolled past a weekend (not the estimate's display label). */
  dueDate: string;
  /** This period's required installment, federal + state. */
  requiredInstallment: number;
  /** What still has to be paid by `dueDate`: the cumulative shortfall after withholding and payments. */
  amountDue: number;
}

/** The figures a harvest changes, read from one full tax estimate. */
export interface HarvestTaxSnapshot {
  totalTax: number;
  /** Federal income tax including AMT (NIIT is separate). */
  federalTax: number;
  stateTax: number;
  niitTax: number;
  taxableShortTermGain: number;
  taxableLongTermGain: number;
  capitalLossDeduction: number;
  /** Capital loss carried to next year. */
  carryforwardToNextYear: number;
  /** Tax still owed after withholding and payments (the April balance, before further installments). */
  balanceDue: number;
  safeHarborMethod: SafeHarborDetermination['selectedMethod'] | 'unknown';
  /** The federal required annual payment (lesser safe-harbor alternative); null when unavailable. */
  requiredAnnualPayment: number | null;
  /** Null when every installment due date for the year has passed. */
  nextInstallment: HarvestInstallment | null;
}

/** Before − after for the figures the plan changes. Positive = the plan lowers it. */
export interface HarvestSavings {
  totalTax: number;
  federalTax: number;
  stateTax: number;
  niitTax: number;
  balanceDue: number;
  /** Drop in the amount due by the next installment date; null when there is no upcoming installment. */
  nextInstallment: number | null;
}

export type HarvestSymbolWarningKind = 'recentPurchase' | 'openShortPut' | 'dividendReinvestment';

export interface HarvestSymbolWarning {
  symbol: string;
  kind: HarvestSymbolWarningKind;
  message: string;
}

/** A position left out entirely because its lots cannot be trusted. */
export interface HarvestExcludedPosition {
  symbol: string;
  accountId: string;
  accountName: string;
  reason: string;
}

export interface HarvestPlanSummary {
  sales: HarvestSale[];
  totalProceeds: number;
  /** Sum of realizedLoss. Negative (or 0 for an empty plan). */
  totalLoss: number;
  shortTermLoss: number;
  longTermLoss: number;
  /** True when the eligible losses were enough to reach the target. */
  targetMet: boolean;
  /** What is still left to offset after the plan (0 when the target is met). */
  remainingToTarget: number;
}

export interface TaxLossHarvestPlan {
  taxYear: number;
  /** The trade date the plan assumes: the next market session (today, during or before one). */
  asOf: string;
  target: HarvestTarget;
  /** Recognized gains so far this year (before carryforward) and the carryforward coming in. */
  realizedToDate: { shortTerm: number; longTerm: number; carryforwardIn: number };
  /** Eligible unrealized losses by term (negative). */
  availableLosses: { shortTerm: number; longTerm: number };
  plan: HarvestPlanSummary;
  before: HarvestTaxSnapshot;
  after: HarvestTaxSnapshot;
  savings: HarvestSavings;
  /** Every loss lot in a taxable account, whatever its status, largest loss per dollar first. */
  lots: HarvestLot[];
  symbolWarnings: HarvestSymbolWarning[];
  excludedPositions: HarvestExcludedPosition[];
  assumptions: string[];
  /** Always true: a planning estimate, never a filing figure. */
  isEstimate: true;
}

/** Request body for `POST /api/v1/tax/harvest-plan`. */
export interface TaxLossHarvestPlanRequest {
  portfolioId: string;
  target?: HarvestTarget;
  /** Lots with a smaller loss than this (dollars) are not sold. */
  minLossPerLot?: number;
  filingStatus?: string;
  state?: string;
  /** Prior-year federal tax, as the Taxes page sends it (0/absent = saved settings). */
  priorYearTax?: number;
  /** Capital-loss carryforward, as the Taxes page sends it (absent = saved settings). */
  capitalLossCarryforward?: number;
  projectFullYear?: boolean;
  includeOpenOptions?: boolean;
}
